/**
 * "Put this on your Home Screen" — the OFFICE's half, in English.
 *
 * A driver MUST install before enrolling: the one-time link is spent by
 * whichever of Safari and the icon gets there first, so their screens
 * (IosInstallScreens, from Enrolment) are a gate. Office staff have just
 * signed in with a password and may well be at a desk, so this is an OFFER:
 * dismissible, never between a dispatcher and the board, and silent wherever
 * it would be nonsense — already installed, or on a desktop, which has no
 * Home Screen to add anything to.
 *
 * WHAT IS SHARED WITH THE DRIVER'S SCREENS, AND WHAT IS NOT
 * ---------------------------------------------------------
 * Shared: the numbered `Steps`/`Step` blocks, the drawn Share and "Add to
 * Home Screen" glyphs, the app's own icon, and the three-tap iPhone sequence
 * itself (`IosAddToHomeSteps`) — one description of what iOS shows, so the
 * two audiences can never be told to look for different buttons.
 * Not shared: the words. The driver's are Hungarian and stay exactly where
 * they were; these are English, and the third tap says something different
 * on purpose (an iPhone icon has its own storage, so a dispatcher signs in
 * again inside it — a driver's icon spends their link and signs itself in).
 *
 * EACH PLATFORM GETS WHAT IT REALLY HAS
 * -------------------------------------
 *   Android/Chrome with `beforeinstallprompt`  a real Install button, which
 *                                              is the browser's own prompt
 *   Android without one (Firefox, Samsung)     the menu, in words
 *   iPhone/iPad in Safari                      Share → Add to Home Screen
 *   iPhone/iPad in any other browser           only Safari can do it
 *   desktop, or already installed              nothing at all
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  IosAddToHomeSteps, Step, Steps,
} from './IosInstallScreens';
import { AddToHomeIcon } from './Icons';
import { detectInstallOffer, INSTALL_OFFERS } from '../installContext';
import { dismissOffer, isDismissed } from '../deviceMemory';

/** The key the office's dismissal of the banner is remembered under. */
export const ADD_TO_HOME_KEY = 'add-to-home-dispatcher';

export const ADD_TO_HOME_COPY = {
  head: 'Add the board to your Home Screen',
  body: 'It opens in one tap, without the browser.',
  /** Said only on an iPhone, where it is the plain truth. */
  iosBody: 'On an iPhone it is also the only place notifications can work, '
    + 'so this is one step, not two.',
  install: 'Install',
  /** The three taps, in the shape IosAddToHomeSteps takes. */
  iosSteps: {
    share: { before: 'Tap', name: 'Share', hint: 'If you cannot see it, tap ⋯ first.' },
    add: { before: 'Scroll down and choose', name: 'Add to Home Screen' },
    open: {
      before: 'Open the new',
      name: 'PH Sofőr',
      after: 'icon — the same app the drivers use — and sign in there once. '
        + 'An iPhone icon keeps its own session.',
    },
  },
  safariFirst: [
    'Open this board in Safari',
    'Tap the Share button there',
    'Choose “Add to Home Screen”',
  ],
  androidMenu: [
    'Open the browser menu (⋮)',
    'Choose “Install app” or “Add to Home screen”',
  ],
};

/**
 * What to offer here, whether it has been waved away, and the browser's own
 * install prompt if it ever hands us one.
 *
 * `beforeinstallprompt` fires AFTER the page has loaded, so what is on offer
 * can change from "the menu, in words" to "a real button" a moment in. The
 * event is kept (it is the only way to raise that prompt) and the answer is
 * worked out again when it arrives.
 */
export function useAddToHomeOffer() {
  const [canPrompt, setCanPrompt] = useState(false);
  // Installing does NOT make this tab standalone — that only happens the
  // next time the icon is opened — so `appinstalled` is the only thing that
  // can stop this page going on offering what it has just been given.
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => isDismissed(ADD_TO_HOME_KEY));
  const prompt = useRef(null);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      prompt.current = event;
      setCanPrompt(true);
    };
    const onInstalled = () => {
      prompt.current = null;
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt.current) return;
    try {
      await prompt.current.prompt();
    } catch {
      // Dismissed, or installed in another tab. Either way the event is
      // spent: a browser hands it out once.
    }
    prompt.current = null;
    setCanPrompt(false);
  }, []);

  const dismiss = useCallback(() => {
    dismissOffer(ADD_TO_HOME_KEY);
    setDismissed(true);
  }, []);

  return {
    offer: installed ? INSTALL_OFFERS.NONE : detectInstallOffer(undefined, { canPrompt }),
    dismissed,
    dismiss,
    install,
  };
}

/** A plain numbered list of sentences — the two cases with no glyphs to draw. */
function PlainSteps({ steps }) {
  return (
    <Steps>
      {steps.map((step, index) => <Step number={index + 1} key={step}>{step}</Step>)}
    </Steps>
  );
}

/**
 * How to do it here, as a card. `offer` is `installContext`'s answer, so
 * this component never sniffs anything for itself.
 */
export default function AddToHomeOffer({ offer, onInstall }) {
  if (offer === INSTALL_OFFERS.NONE) return null;

  const ios = offer === INSTALL_OFFERS.IOS_STEPS || offer === INSTALL_OFFERS.IOS_SAFARI_FIRST;

  return (
    <div className="pp-a2hs">
      <div className="pp-a2hs__head">
        <AddToHomeIcon className="pp-a2hs__icon" size={20} />
        <span>{ADD_TO_HOME_COPY.head}</span>
      </div>
      <div className="pp-a2hs__body">
        {ios ? `${ADD_TO_HOME_COPY.body} ${ADD_TO_HOME_COPY.iosBody}` : ADD_TO_HOME_COPY.body}
      </div>

      {offer === INSTALL_OFFERS.PROMPT ? (
        <button
          type="button"
          className="pp-btn pp-btn--primary pp-btn--block pp-gap-top"
          onClick={onInstall}
        >
          {ADD_TO_HOME_COPY.install}
        </button>
      ) : null}

      {offer === INSTALL_OFFERS.IOS_STEPS ? (
        <IosAddToHomeSteps copy={ADD_TO_HOME_COPY.iosSteps} />
      ) : null}

      {offer === INSTALL_OFFERS.IOS_SAFARI_FIRST ? (
        <PlainSteps steps={ADD_TO_HOME_COPY.safariFirst} />
      ) : null}

      {offer === INSTALL_OFFERS.ANDROID_STEPS ? (
        <PlainSteps steps={ADD_TO_HOME_COPY.androidMenu} />
      ) : null}
    </div>
  );
}
